// RichText helpers (spec §4.2): paragraphs of runs; only bold, italic, link and
// bullet lists. Shared so the server can validate and normalise the same way.
import type { RichText, RichTextBlock, RichTextRun } from './types.ts';

/** Max serialised size of one object's rich text (spec §7.2). */
export const MAX_RICH_TEXT_BYTES = 10_000;

export function fromPlainText(s: string): RichText {
  return normalizeRichText({ blocks: s.split(/\r\n|\r|\n/).map((t) => ({ runs: [{ t }] })) });
}

export function plainText(rt: RichText | undefined): string {
  return rt ? rt.blocks.map((b) => b.runs.map((r) => r.t).join('')).join('\n') : '';
}

export function isEmptyRichText(rt: RichText | undefined): boolean {
  return !rt || rt.blocks.every((b) => b.runs.every((r) => !/\S/.test(r.t)));
}

export function richTextEquals(a: RichText | undefined, b: RichText | undefined): boolean {
  return JSON.stringify(normalizeRichText(a ?? { blocks: [] })) === JSON.stringify(normalizeRichText(b ?? { blocks: [] }));
}

export function richTextSize(rt: RichText): number {
  return JSON.stringify(rt).length;
}

/**
 * Only http(s) and mailto links survive; scheme-less input is treated as a
 * web address. Anything else (javascript:, data:, ...) is dropped.
 */
export function sanitizeHref(href: string | null | undefined): string | null {
  const s = (href ?? '').trim();
  if (!s || /\s/.test(s)) return null;
  if (/^(https?:\/\/|mailto:)/i.test(s)) return s;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  if (/^[\w-]+(\.[\w-]+)+/.test(s)) return `https://${s}`;
  return null;
}

const sameMarks = (a: RichTextRun, b: RichTextRun) => a.b === b.b && a.i === b.i && a.href === b.href;

/**
 * Canonical form: marks only where set, adjacent runs with equal marks merged,
 * empty runs removed, trailing blank blocks removed.
 */
export function normalizeRichText(rt: RichText): RichText {
  const blocks: RichTextBlock[] = rt.blocks.map((block) => {
    const runs: RichTextRun[] = [];
    for (const r of block.runs) {
      if (!r.t) continue;
      const run: RichTextRun = { t: r.t };
      if (r.b) run.b = 1;
      if (r.i) run.i = 1;
      const href = sanitizeHref(r.href);
      if (href) run.href = href;
      const prev = runs[runs.length - 1];
      if (prev && sameMarks(prev, run)) prev.t += run.t;
      else runs.push(run);
    }
    return block.list === 'bullet' ? { list: 'bullet', runs } : { runs };
  });
  while (blocks.length && blocks[blocks.length - 1]!.runs.every((r) => !/\S/.test(r.t))) blocks.pop();
  return { blocks };
}

/** Drops trailing content until the serialised size fits. */
export function truncateRichText(rt: RichText, maxBytes = MAX_RICH_TEXT_BYTES): RichText {
  if (richTextSize(rt) <= maxBytes) return rt;
  const out: RichText = { blocks: [] };
  for (const block of rt.blocks) {
    const b: RichTextBlock = block.list ? { list: block.list, runs: [] } : { runs: [] };
    out.blocks.push(b);
    for (const run of block.runs) {
      b.runs.push({ ...run });
      if (richTextSize(out) > maxBytes) {
        const last = b.runs[b.runs.length - 1]!;
        // Trim the overflowing run to what fits (JSON escaping makes this approximate, so re-check).
        while (last.t && richTextSize(out) > maxBytes) last.t = last.t.slice(0, Math.floor(last.t.length * 0.9));
        return normalizeRichText(out);
      }
    }
  }
  return normalizeRichText(out);
}
